// request.js - axios拦截器配置

import axios from 'axios'
import { getToken, clearAuth } from './auth'
import { Message, Loading } from 'element-ui'

// 创建axios实例
const service = axios.create({
  baseURL: '/api', // 基础URL
  timeout: 30000 // 请求超时时间（增加到30秒以适应ngrok延迟）
})

// ========= 全局 Loading（并发计数 + 防闪烁 + 重试兼容） =========
let loadingInstance = null
let activeRequestCount = 0

const startGlobalLoading = () => {
  if (loadingInstance) return
  loadingInstance = Loading.service({
    lock: true,
    fullscreen: true,
    background: 'transparent',
    customClass: 'kh-global-loading',
    spinner: 'kh-loading-spinner',
    text: '数据加载中...'
  })
}

const stopGlobalLoading = () => {
  if (loadingInstance) {
    loadingInstance.close()
    loadingInstance = null
  }
}

const incRequest = () => {
  activeRequestCount += 1
  if (activeRequestCount === 1) {
    startGlobalLoading()
  }
}

const decRequest = () => {
  activeRequestCount = Math.max(activeRequestCount - 1, 0)
  if (activeRequestCount === 0) {
    stopGlobalLoading()
  }
}

// 重试配置
const MAX_RETRIES = 2 // 最大重试次数
const RETRY_DELAY = 1000 // 重试延迟（毫秒）

// 延迟函数
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms))

// 判断是否应该重试
const shouldRetry = (error, config) => {
  // 如果已经重试过最大次数，不再重试
  if (config.__retryCount >= MAX_RETRIES) {
    return false
  }
  
  // 只对GET请求和幂等操作进行重试
  const method = config.method ? config.method.toLowerCase() : ''
  if (method !== 'get' && method !== 'head' && method !== 'options') {
    return false
  }
  
  // 对超时错误和网络错误进行重试
  if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
    return true // 超时错误
  }
  
  if (!error.response && error.request) {
    return true // 网络错误（请求已发送但没有收到响应）
  }
  
  return false
}

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 全局 loading（同一个请求重试时不重复计数）
    if (!config.__kh_hasLoading) {
      config.__kh_hasLoading = true
      incRequest()
    }

    // 在请求头中添加token
    const token = getToken()
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    }
    return config
  },
  error => {
    // 处理请求错误
    console.error('请求错误:', error)
    if (error && error.config && error.config.__kh_hasLoading) {
      error.config.__kh_hasLoading = false
      decRequest()
    }
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    if (response && response.config && response.config.__kh_hasLoading) {
      response.config.__kh_hasLoading = false
      decRequest()
    }
    const data = response.data
    // 部分后端用 200 返回 body 内 code=401 表示未认证，统一按登录失效处理
    if (data && (data.code === 401 || data.code === 'UNAUTHORIZED')) {
      Message.error(data.message || '登录已过期，请重新登录')
      clearAuth()
      window.location.href = '/login'
      return Promise.reject(new Error(data.message || 'Unauthorized'))
    }
    return data
  },
  async error => {
    // 处理响应错误
    console.error('响应错误:', error)
    
    const config = error.config
    
    // 初始化重试计数器
    if (!config.__retryCount) {
      config.__retryCount = 0
    }
    
    // 检查是否应该重试
    if (shouldRetry(error, config)) {
      config.__retryCount += 1
      
      console.log(`请求失败，正在进行第 ${config.__retryCount} 次重试...`)
      
      // 等待重试延迟
      await delay(RETRY_DELAY)
      
      // 重新发起请求
      return service(config)
    }

    // 不再重试：结束 loading 计数
    if (config && config.__kh_hasLoading) {
      config.__kh_hasLoading = false
      decRequest()
    }
    
    // 统一错误处理
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // 未授权，清除认证信息并跳转到登录页
          Message.error('登录已过期，请重新登录')
          clearAuth()
          window.location.href = '/login'
          break
        case 403:
          Message.error('没有权限访问该资源')
          break
        case 404:
          Message.error('请求的资源不存在')
          break
        case 500:
          Message.error('服务器内部错误')
          break
        default:
          Message.error(`请求失败: ${error.response.data.message || '未知错误'}`)
      }
    } else if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
      // 超时错误的特殊处理
      if (config.__retryCount >= MAX_RETRIES) {
        Message.error('请求超时，请检查网络连接或稍后重试')
      }
    } else if (error.request) {
      // 请求已发送但没有收到响应
      if (config.__retryCount >= MAX_RETRIES) {
        Message.error('网络错误，服务器无响应，请检查网络连接')
      }
    } else {
      // 请求配置错误
      Message.error(`请求配置错误: ${error.message}`)
    }
    
    return Promise.reject(error)
  }
)

export default service